import React, { useRef, useEffect, useState } from 'react';
import { func, string, arrayOf, shape } from 'prop-types';
import { Form as FinalForm, Field, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import { intlShape, injectIntl } from '../../../util/reactIntl';
import {
  Button,
  Form,
  LocationAutocompleteInput,
  Icons,
  FieldDateRangeController,
  IconSearch,
} from '../../../components';
import { compose } from 'redux';
import { withViewport } from '../../../util/uiHelpers';
import { getStartOf } from '../../../util/dates';

import css from './LandingSearchForm.module.css';

const identity = v => v;

// Transform purposeOptions to match react-select format
const transformPurposeOptions = options =>
  options.map(({ option, label }) => ({
    value: option,
    label,
  }));

const LocationSearchField = props => {
  const { intl, handleChange, isLandingLocation, isSearchPageFilter, locationRef } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        const searchOnChange = value => {
          onChange(value);
          handleChange(value);
        };

        const searchInput = { ...restInput, onChange: searchOnChange };
        return (
          <div className={css.locationFld} ref={locationRef}>
            {/* <label htmlFor="searchInput">
              {intl.formatMessage({ id: 'LandingSearchForm.destination' })}
            </label> */}
            <LocationAutocompleteInput
              placeholder={intl.formatMessage({ id: 'LandingSearchForm.location' })}
              iconClassName={css.searchInputIcon}
              inputClassName={css.searchInput}
              predictionsClassName={css.searchPredictions}
              input={searchInput}
              meta={meta}
              rootClassName={isLandingLocation && css.landingLocationSearch}
              isLandingLocation={isLandingLocation}
              isSearchPageFilter={isSearchPageFilter}
              locationRef={locationRef}
            />
          </div>
        );
      }}
    />
  );
};

const LandingSearchFormComponent = props => {
  const today = new Date();
  const tomorrow = new Date(today);
  const datePickerRef = useRef(null);
  const locationRef = useRef(null);
  tomorrow.setDate(today.getDate() + 1);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dateFormattingOptions = { month: 'short', day: 'numeric' };
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const isOutsideRange = day => {
    const localizedDay = getStartOf(day, 'day', timeZone);
    const todayStart = getStartOf(today, 'day', timeZone);
    return localizedDay < todayStart;
  };

  const handleClearDates = form => {
    form.change('dates', null);
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        isCalendarOpen &&
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          values,
          intl,
          form,
          isSearchPageFilter,
          isTopbarSearch,
          showTopbarSearch,
        } = formRenderProps;

        const classes = classNames(rootClassName || css.root, className, {
          [css.searchPageFilter]: isSearchPageFilter,
          [css.topbarSearchForm]: isTopbarSearch,
          [css.showTopbarSearch]: showTopbarSearch,
        });

        const hasDates = values.dates && (values.dates.startDate || values.dates.endDate);
        return (
          <Form className={classes} onSubmit={e => e.preventDefault()}>
            <FormSpy
              subscription={{ values: true }}
              onChange={({ values }) => {
                const { startDate, endDate } = values?.dates || {};

                // only proceed once you have both a real start and end
                if (!startDate || !endDate) return;

                // close the calendar
                setIsCalendarOpen(false);

                // if they picked the same day, bump endDate by one day
                if (startDate.getTime() === endDate.getTime()) {
                  const nextDay = new Date(startDate);
                  nextDay.setDate(startDate.getDate() + 1);

                  // write back a real Date object — not just the day number
                  form.change('dates.endDate', nextDay);
                }
              }}
            />

            <div className={css.formBody}>
              <LocationSearchField
                intl={intl}
                locationRef={locationRef}
                handleChange={location => {
                  if (location.selectedPlace) {
                    form.change('location', location);
                  }
                }}
                isLandingLocation={true}
                isSearchPageFilter={isSearchPageFilter}
                isTopbarSearch={isTopbarSearch}
              />

              {/* <span className={css.lineSeparator}></span> */}
              <div className={css.dateRangePickerContainer}>
                {/* <label htmlFor="dateSearch">
                  {intl.formatMessage({ id: 'LandingSearchForm.date' })}
                </label> */}
                <div className={css.dateRangePickerWrapper} ref={datePickerRef}>
                  <Field
                    name="dates"
                    render={({ input: { value, onChange }, meta }) => {
                      const handleDateChange = selectedDate => {
                        form.change('dates', null); // Clear existing dates
                        if (!selectedDate) return;

                        const startDate = selectedDate;
                        // Pull whatever endDate might already be in the form (or null)
                        let endDate = values?.dates?.endDate
                          ? new Date(values.dates.endDate)
                          : null;

                        // Propagate both dates back into final-form
                        onChange({ startDate, endDate });
                      };

                      const selectedStartDate = value?.startDate || null;
                      const selectedEndDate = value?.endDate || null;
                      // const dateInputText = selectedStartDate
                      //   ? intl.formatDate(selectedStartDate, dateFormattingOptions)
                      //   : '';
                      const dateInputText = selectedStartDate
                        ? selectedEndDate
                          ? `${intl.formatDate(
                              selectedStartDate,
                              dateFormattingOptions
                            )} - ${intl.formatDate(selectedEndDate, dateFormattingOptions)}`
                          : intl.formatDate(selectedStartDate, dateFormattingOptions)
                        : '';
                      return (
                        <div className={css.customDateField}>
                          <Icons name="calendar" />
                          <input
                            type="text"
                            value={dateInputText}
                            placeholder={intl.formatMessage({
                              id: 'LandingSearchForm.datePlaceholder',
                            })}
                            readOnly
                            className={css.dateInput}
                            onClick={() => setIsCalendarOpen(true)}
                          />
                          {isCalendarOpen && (
                            <div className={css.calendarOverlay}>
                              <FieldDateRangeController
                                name="dates"
                                minimumNights={0}
                                isDaily={true}
                                isOpen={isCalendarOpen}
                                onChange={selectedDates => {
                                  // Single date selected
                                  if (selectedDates && selectedDates.startDate) {
                                    handleDateChange(selectedDates.startDate);
                                  }
                                }}
                                onClose={() => setIsCalendarOpen(false)}
                                isOutsideRange={isOutsideRange}
                                useMobileMargins
                                isSingleDatePicker
                              />
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />

                  {hasDates && (
                    <div
                      className={css.clearButton}
                      onClick={() => handleClearDates(form)}
                      type="button"
                    >
                      <Icons name="closeIcon" />
                    </div>
                  )}
                </div>
              </div>

              <Button
                className={classNames({
                  [css.searchIcon]: isSearchPageFilter,
                  [css.searchBtn]: !isSearchPageFilter,
                })}
                onClick={() => props.onSubmit(values)}
                type="submit"
              >
                {isTopbarSearch ? (
                  <IconSearch />
                ) : (
                  intl.formatMessage({ id: 'LandingSearchForm.search' })
                )}
              </Button>
            </div>
          </Form>
        );
      }}
    />
  );
};

LandingSearchFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  purposeOptions: [],
};

LandingSearchFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  onSubmit: func.isRequired,
  intl: intlShape.isRequired,
  purposeOptions: arrayOf(
    shape({
      option: string.isRequired,
      label: string.isRequired,
    })
  ),
};

const LandingSearchForm = compose(withViewport, injectIntl)(LandingSearchFormComponent);

export default LandingSearchForm;
